export class RequestError extends Error {
  readonly errors?: unknown[];
  readonly code?: string;

  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    const details =
      body && typeof body === "object"
        ? (body as Record<string, unknown>)
        : undefined;
    super(
      typeof details?.message === "string"
        ? details.message
        : typeof body === "string" && body
          ? body
          : `Request failed (${status})`,
    );
    this.name = "RequestError";
    this.code = typeof details?.code === "string" ? details.code : undefined;
    this.errors = Array.isArray(details?.errors) ? details.errors : undefined;
  }
}

/** Transport shared by web and a future native client; auth and base URL are injected. */
export class Request {
  constructor(
    private readonly options: {
      baseUrl?: string;
      getAccessToken?: () => Promise<string | undefined>;
      onError?: (error: RequestError) => void;
    },
  ) {}

  async request<T = unknown>(
    path: string,
    options: RequestInit = {},
  ): Promise<[Response, T]> {
    const headers = new Headers(options.headers);
    const token = await this.options.getAccessToken?.();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    if (!headers.has("Accept")) {
      headers.set("Accept", "application/json");
    }
    if (typeof options.body === "string" && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    const response = await fetch(`${this.options.baseUrl ?? ""}${path}`, {
      ...options,
      headers,
    });
    const text = await response.text();
    let body: unknown = text;
    if (
      text &&
      response.headers.get("Content-Type")?.includes("application/json")
    ) {
      try {
        body = JSON.parse(text);
      } catch {
        /* Preserve non-JSON error responses. */
      }
    }
    if (!response.ok) {
      const error = new RequestError(response.status, body);
      this.options.onError?.(error);
      throw error;
    }
    return [response, body as T];
  }
}
